import React from 'react'
import { Form, Field } from 'react-final-form'
import Button from '../atoms/Button'
import View from '../atoms/View'
import TextNew from '../atoms/TextNew'
import DatePicker from '../atoms/DatePicker'
import ClientSearchSelect from './ClientSearchSelect'
import VehicleSearchSelect from './VehicleSearchSelect'
import Switcher from '../atoms/Switcher'
import { ImportDocumentFormData, validateImportDocumentForm } from 'src/helpers/importDocumentFormValidation'

interface ImportDocumentFormProps {
  initialValues: Partial<ImportDocumentFormData>
  onSubmit: (values: ImportDocumentFormData) => void
  onCancel: () => void
  title: string
  submitButtonText: string
  loading?: boolean
}

const ImportDocumentForm = ({
  initialValues,
  onSubmit,
  onCancel,
  title,
  submitButtonText,
  loading = false
}: ImportDocumentFormProps): JSX.Element => {
  return (
    <div className="h-[calc(100vh-200px)]">
      <View className="flex justify-between items-center mb-6">
        <TextNew className="text-2xl font-SemiBold">{title}</TextNew>
      </View>

      <Form
        onSubmit={onSubmit}
        validate={validateImportDocumentForm}
        initialValues={initialValues}
        render={({ handleSubmit, submitting, pristine, hasValidationErrors, form }) => (
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          <form onSubmit={handleSubmit}>
            <div className="bg-white rounded-4 shadow-bothMedium p-6">
              <div className="grid grid-cols-12 gap-6">
                <div className="col-span-12">
                  <TextNew className="text-xl font-SemiBold text-darkGrey mb-4">Client Selection</TextNew>
                </div>

                <div className="col-span-12">
                  <View className="flex flex-col gap-2">
                    <div className="flex items-center gap-4 bg-lightGrey rounded-4 p-4">
                      <div className="flex-1">
                        <ClientSearchSelect
                          selectedId={
                            typeof form.getState().values.client_id === 'number' &&
                            form.getState().values.client_id !== 0
                              ? String(form.getState().values.client_id)
                              : ''
                          }
                          selectedLabel={form.getState().values.client_name ?? ''}
                          placeholder="Select client"
                          onSelect={(client) => {
                            form.change('client_id', parseInt(client.id, 10))
                            form.change('client_name', client.name)
                          }}
                          onClear={() => {
                            form.change('client_id', 0)
                            form.change('client_name', '')
                          }}
                        />
                      </div>
                    </div>
                  </View>
                </div>

                <div className="col-span-12">
                  <TextNew className="text-xl font-SemiBold text-darkGrey mb-4">Motorbike Selection</TextNew>
                </div>

                <div className="col-span-12">
                  <View className="flex flex-col gap-2">
                    <div className="flex items-center gap-4 bg-lightGrey rounded-4 p-4">
                      <div className="flex-1">
                        <VehicleSearchSelect
                          selectedId={
                            typeof form.getState().values.motorbike_id === 'number' &&
                            form.getState().values.motorbike_id !== 0
                              ? String(form.getState().values.motorbike_id)
                              : ''
                          }
                          selectedLabel={form.getState().values.motorbike_name ?? ''}
                          placeholder="Select motorbike"
                          onSelect={(motorbike) => {
                            form.change('motorbike_id', parseInt(motorbike.id, 10))
                            form.change('motorbike_name', motorbike.model)
                          }}
                          onClear={() => {
                            form.change('motorbike_id', 0)
                            form.change('motorbike_name', '')
                          }}
                        />
                      </div>
                    </div>
                  </View>
                </div>

                <div className="col-span-12">
                  <TextNew className="text-xl font-SemiBold text-darkGrey">Import Date</TextNew>
                </div>

                <div className="col-span-6">
                  <Field name="import_date">
                    {({ input, meta }) => (
                      <DatePicker
                        label="Import Date *"
                        value={input.value}
                        onChange={(value) => {
                          input.onChange(value)
                        }}
                        containerStyles={{
                          width: '100%',
                          boxShadow: '0px 0px 5px rgba(0,0,0,0.3)',
                          borderRadius: '15px'
                        }}
                        error={Boolean(meta.error) && Boolean(meta.touched)}
                      />
                    )}
                  </Field>
                </div>
                <div className="col-span-12">
                  <TextNew className="text-xl font-SemiBold text-darkGrey">With Plates</TextNew>
                </div>
                <div className="col-span-6">
                  <Field name="with_plates">
                    {({ input, meta }) => (
                      <Switcher
                        value={input.value}
                        onSwitch={(value) => {
                          input.onChange(value)
                        }}
                        options={[
                          { value: true, label: 'Yes' },
                          { value: false, label: 'No' }
                        ]}
                      />
                    )}
                  </Field>
                </div>
              </div>

              <div className="h-6" />

              <View className="flex gap-4 justify-end">
                <Button
                  onClick={onCancel}
                  customStyles="bg-lightGrey text-darkGrey px-6 py-3 rounded-2 hover:bg-grey transition-colors">
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    void handleSubmit()
                  }}
                  disabled={submitting || pristine || hasValidationErrors || loading}
                  customStyles="bg-groupBlue text-white px-6 py-3 rounded-2 hover:bg-darkBlue transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                  {submitting || loading ? 'Saving...' : submitButtonText}
                </Button>
              </View>
            </div>
          </form>
        )}
      />
    </div>
  )
}

export default ImportDocumentForm
